import { useRef, useState } from 'react';

import { cn } from '@/utils/cn';

import { Button } from './Button';
import { Icon } from './Icon';

/** Drag-and-drop file input with client-side size and type pre-checks.
 *  The backend re-validates everything, including magic bytes. */
export function FileUploader({ accept, maxSizeBytes, isUploading = false, hint, onSelect }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);

  function validate(file) {
    const allowed = accept.split(',').map((value) => value.trim().toLowerCase());
    const extension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    const matches = allowed.some(
      (value) => value === file.type.toLowerCase() || value === extension,
    );
    if (!matches) return `Unsupported file type. Allowed: ${accept}`;
    if (file.size > maxSizeBytes) {
      return `File is too large. Maximum ${Math.round(maxSizeBytes / (1024 * 1024))} MB.`;
    }
    return null;
  }

  function handleFile(file) {
    if (!file) return;
    const message = validate(file);
    setError(message);
    if (!message) onSelect(file);
  }

  function onDrop(event) {
    event.preventDefault();
    setDragging(false);
    handleFile(event.dataTransfer.files?.[0]);
  }

  function onChange(event) {
    handleFile(event.target.files?.[0]);
    event.target.value = '';
  }

  return (
    <div className="space-y-2">
      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          // Neutral at rest; the brand colour only answers the pointer or a
          // file held over it, so the zone does not compete with the page.
          'rounded-card flex flex-col items-center border border-dashed px-5 py-6 text-center transition',
          dragging
            ? 'border-brand-500 bg-brand-50'
            : 'border-ink-muted/30 bg-surface-muted/50 hover:border-brand-300 hover:bg-brand-50/40',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'grid size-10 place-items-center rounded-lg ring-1 transition ring-inset',
            dragging
              ? 'bg-brand-600 text-white ring-brand-600'
              : 'bg-surface text-brand-600 ring-line shadow-xs',
          )}
        >
          <Icon name="upload" size={18} strokeWidth={2} />
        </span>
        <p className="text-ink mt-3 text-[13px] font-medium">
          {dragging ? 'Drop it here' : 'Drag and drop your file here'}
        </p>
        <p className="text-ink-muted mt-0.5 text-xs">or</p>
        <Button
          type="button"
          size="sm"
          className="mt-2.5"
          isLoading={isUploading}
          onClick={() => inputRef.current?.click()}
        >
          {!isUploading && <Icon name="upload" size={14} />}
          {isUploading ? 'Uploading…' : 'Browse files'}
        </Button>
        {hint && <p className="text-ink-muted mt-3 text-[11px]">{hint}</p>}
        <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={onChange} />
      </div>
      {error && (
        <p className="text-caption text-danger-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
